import React from 'react';
import {Modal} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {X} from '../../../icons';
import {SourcesLibrary} from './SourcesLibrary';
import {SourcesDrawerContext, useSourcesDrawer} from './sourcesDrawerContext';

/**
 * «Documentos y notas»: la biblioteca del material que alimenta la memoria, en un **modal
 * centrado** que se abre a pedido — y NO como un bloque claro incrustado en el acto Alimenta.
 *
 * Lo de «no incrustado» viene del 03/09: con la tabla de Fuentes montada inline, el acto Alimenta
 * arrancaba con el orbe y terminaba con veinte filas más las conexiones de Drive, y el umbral hacia
 * Revisa quedaba a dos pantallas. El material se consulta y se gestiona; no se lee de corrido.
 *
 * Lo de «modal centrado» es del 08/09, y reemplaza al panel LATERAL que resolvió aquello: fede («me
 * gusta que sea un modal en el centro de la pantalla y no un side panel») y Pablo («sí, también»)
 * eligieron la forma del mockup (`#docsPanel` / `.dp-sheet`) sobre la hoja que salía del borde. La
 * hoja es la forma de algo auxiliar que acompaña a la página; esto es el inventario completo del
 * material del cliente, y merece el centro.
 *
 * Adentro va `SourcesLibrary`, que es una vista NUEVA — no `MemorySourcesView` re-alojada. Ahí está
 * el resto de la decisión del 08/09 (una sola lista, filtros, filas en vez de tabla, sin el strip
 * del Paquete de Contexto), y por eso la Memoria clásica no se entera de nada de esto.
 *
 * Es un `Modal` de reactstrap, a propósito: hereda el foco al abrir, el Escape, el click en el
 * fondo y la clase `modal-open` del body — que es la guarda con la que el mazo de Revisa apaga su
 * teclado mientras hay algo abierto encima.
 *
 * ⚠️ El componente y el archivo conservan el nombre `SourcesDrawer` (y el contexto,
 * `sourcesDrawerContext`) aunque ya no sea un drawer: es el mismo objeto, que cambió de forma, y
 * renombrarlo tocaría los cuatro consumidores y sus tests sin cambiar una sola línea de conducta.
 * Las clases CSS sí se renombraron a `iab-sheet`, porque ésas se leen en el DOM y en el QA.
 */

// La manija (`useSourcesDrawer`) y su contexto viven en `sourcesDrawerContext.js`, que es liviano:
// los consumidores no tienen por qué arrastrar la vista de la biblioteca entera. Se re-exportan acá
// para quien quiera importar todo del panel.
export {SourcesDrawerContext, useSourcesDrawer};

export function SourcesDrawer({
    clientId,
    clientName,
    isOpen,
    onClose,
    refreshToken = 0,
    // Sube cuando quien abre el modal quiere además abrir el selector de Drive (el botón de la
    // Barra Total). Es un contador y no un booleano para que dos pedidos seguidos abran dos veces.
    connectToken = 0,
    // Material entrado por el botón «Cargar documento» de adentro del modal. Sube al contenedor,
    // que es quien despierta a la lista de ingesta, al narrador y al mazo de Revisa: los tres viven
    // en la página de atrás y no se enteran solos.
    onIngested,
    // Los hechos cambiaron por un borrado con cascada: el mazo de Revisa tiene que
    // soltar las cartas del documento que ya no está (card VLtk3vc7).
    onFactsChanged,
}) {
    const {t} = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            modalClassName="iab-sheet"
            className="iab-sheet__dialog"
            contentClassName="iab-sheet__content"
            unmountOnClose
            aria-labelledby="iab-sheet-title"
        >
            {/* El «Cerrar» va FUERA de la hoja, flotando arriba a la derecha, como en el mockup
                (`.dp-close`): adentro competiría con el título y con el conteo. */}
            <button type="button" className="iab-sheet__close" onClick={onClose}>
                <X size={13} weight="bold" aria-hidden="true"/>
                {t('ia_booster.sources.close')}
            </button>
            <div className="iab-sheet__body">
                <SourcesLibrary
                    clientId={clientId}
                    clientName={clientName}
                    refreshToken={refreshToken}
                    openConnectToken={connectToken}
                    onIngested={onIngested}
                    onFactsChanged={onFactsChanged}
                    // El que sigue las extracciones en vivo es el acto Alimenta, que además avisa
                    // por `refreshToken` cuando una termina (`handleDocumentReady`). Con el poll
                    // prendido acá volvían las CUATRO consultas cada 8 s que sacar la tabla del
                    // acto vino a resolver el 03/09.
                    pollDocuments={false}
                />
            </div>
        </Modal>
    );
}

export default SourcesDrawer;
